import { assertNever } from '@kofno/piper';
import { action, makeObservable, observable } from 'mobx';
import { fieldsToTemplate, fieldsToValues } from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import {
  LicenseAllocationsFormResource,
  LoadError,
  Ready,
  State,
  SubmitError,
  SubmittedLicenseAllocationsResource,
  Submitting,
  loading,
  loadingError,
  missingTemplate,
  ready,
  submitted,
  submitting,
  submittingError,
  waiting,
} from './Types';

class LicenseAllocationsStore {
  @observable state: State = waiting();

  constructor() {
    makeObservable(this);
  }

  @action waiting = (): void => {
    this.state = waiting();
  };

  @action loading = (link: Link): void => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading-error':
        this.state = loading(link);
        break;
      case 'loading':
      case 'ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action loadingError = (err: LoadError): void => {
    switch (this.state.kind) {
      case 'loading':
        this.state = loadingError(err);
        break;
      case 'waiting':
      case 'loading-error':
      case 'ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action ready = (resource: LicenseAllocationsFormResource): void => {
    switch (this.state.kind) {
      case 'loading':
      case 'submitting':
        const template = resource.templates.self;
        this.state = template ? ready(resource, template) : loadingError(missingTemplate());
        break;
      case 'waiting':
      case 'loading-error':
      case 'ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action submitting = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'submitting-error':
        const { resource, template } = this.state;
        this.state = fieldsToValues(template.fields)
          .mapError(fieldsToTemplate(template))
          .cata<Submitting | Ready>({
            Ok: (values) => submitting(resource, template, values),
            Err: (errTemplate) => ready(resource, errTemplate),
          });
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'submitting':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action submittingError = (error: SubmitError): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submittingError(this.state.resource, this.state.template, error);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action submitted = (resource: SubmittedLicenseAllocationsResource): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitted(resource, this.state.template);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };
}

export default LicenseAllocationsStore;
